import React, { useContext, useEffect, useState } from 'react';
import { MerchantType } from '../../../interfaces/delivery-company/merchants';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import {
  DoubleDate,
  DoubleDateMode,
  DoubleDateWithMode,
} from '../../common/double-date-picker/double-date-picker.interfaces';
import { useImmer } from 'use-immer';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios from 'axios';
import {
  Box,
  Card,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
  Switch,
} from '@mui/material';
import BulkAction from '../../common/bulk-action/bulk-action';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import FilterBar from '../../common/filter-bar/filter-bar';
import SearchBar from '../../common/search-bar/search-bar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
  defaultMerchantFooter,
  defaultMerchantReportFieldsConfig,
} from '../../common/report-builder/fields-config';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { PaginationContext } from '../../../contexts/pagination-context';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import {
  ActorFilterOption,
  DeliveryStateFilterOption,
  IFilterOption,
} from '../../common/filter-bar/types';
import { DeliveryState } from '../../../enums/orders';
import ResponsiveOrdersListing, {
  relevant_fields,
} from '../common/orders-listing';
import PickupTaskDialog from '../pickup-tasks/pickup-task-dialog';
import { OrdersFilterKeys } from '../../common/filter-bar/keys-config';
import DeliveryBatchDialog from '../delivery-tasks/delivery-task-dialog';
import { SortingByColumnState } from '../../common/orders-listing-table/utils';
import DoubleDateOrdersFilter from '../../common/double-date-orders-filter/double-date-orders-filter';
import { generateDateFilterQueryParam } from '../../common/double-date-orders-filter/utils';
import { IQuickFilter } from '../../../interfaces/common/quick-filters';
import NewFeatureChip from '../../common/new-feature-chip';
import { ApplyQuickFilter, UnapplyQuickFilter } from '../../common/apply-quick-filters-manager/apply-quick-filters-manager';

export interface MerchantOrdersProps {
  merchant: MerchantType;
}

function MerchantOrders({ merchant }: MerchantOrdersProps) {
  const [ordersList, setOrdersList] = useState<OrderType[]>([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [incompleteOrdersOnly, setIncompleteOrdersOnly] = useState(false);
  const [includeFrozen, setIncludeFrozen] = useState(false);
  const [dateFilter, setDateFilter] = useState<DoubleDateWithMode>({
    startDate: null,
    endDate: null,
    mode: DoubleDateMode.CREATION_DATE,
  });

  const [showingPickupTask, setShowingPickupTask] = useState<string | null>(
    null
  );
  const [showingDeliveryBatch, setShowingDeliveryBatch] = useState<
    string | null
  >(null);

  const { color } = useContext(OrgSettingsContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedOrders, updateSelectedOrders] = useImmer<
    Map<string, OrderType>
  >(new Map());

  const resetSelectionState = () => {
    updateSelectedOrders((selectedOrders) => {
      selectedOrders.clear();
    });
  };

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const totalSelected = selectedOrders.size;

  const searchOptions = ['Order_Id', 'Ref_Number'];

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
    searchColumn: 'Order_Id',
    searchValue: '',
    searchOperation: 'equals',
  });

  const [filters, setFilters] = useState<IFilterOption[]>([
    new ActorFilterOption(
      {
        actortype: 'merchant',
        val: merchant,
      },
      true
    ),
  ]);

  const [sorting, setSorting] = useState<SortingByColumnState>({
    column: 'creation_date',
    order: 'decreasing',
  });

  useEffect(() => {
    let response: any;
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/orders/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      if (controller.searchColumn !== '' && controller.searchValue !== '') {
        endpoint += `&${controller.searchColumn.toLowerCase()}__${controller.searchOperation}=${encodeURIComponent(controller.searchValue)}`;
      }

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;
      endpoint += `&${generateDateFilterQueryParam(dateFilter)}`;
      endpoint += `&sort_by_${sorting.order}=${encodeURIComponent(sorting.column)}`;
      if (incompleteOrdersOnly) {
        endpoint += `&incomplete_only=true`;
      }

      if (includeFrozen) {
        endpoint += `&include_frozen=true`;
      }

      console.log(endpoint);
      try {
        response = await axios.get<{ count: number; results: OrderType[] }>(
          endpoint,
          config
        );
        setOrdersList(response.data.results);
        setOrdersCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [controller, dateFilter, filters, sorting, incompleteOrdersOnly, includeFrozen]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: 10 });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
      searchColumn: column,
      searchValue: value,
      searchOperation: operation,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
      rowsPerPage: paginationConfig.rowsPerPage,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
    resetSelectionState();
  };

  const dateFilterAction = (dateFilterValue: DoubleDateWithMode) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
    resetSelectionState();
  };

  return (
    <Box>
      <Accordion elevation={0}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box>
                <Typography
                variant="h5"
                component="h5"
                sx={{ textAlign: 'center', mt: 1, mb: 1 }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <ManageSearchIcon fontSize="large" color="primary" />
                  <span>
                    <b>Search and Filter Controls</b>
                  </span>
                </div>
              </Typography>
              </Box>
        </AccordionSummary>
        <AccordionDetails>
          <SearchBar
            searchOptions={searchOptions}
            searchQueryAction={searchQueryAction}
          />
          <br />
          <DoubleDateOrdersFilter dateFilterAction={dateFilterAction} />
          <br />
          <FilterBar
            filterQueryAction={filterQueryAction}
            currentlySelectedFilters={filters}
            filterKeys={OrdersFilterKeys('merchant').filter(
              (item) => item != 'merchant'
            )}
          />
        </AccordionDetails>
      </Accordion>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
          marginLeft: 10,
        }}
      >
        <Chip
          icon={<ChecklistIcon />}
          color="primary"
          label={`Selected ${totalSelected}/${ordersCount}`}
          variant={
            totalSelected === ordersCount && totalSelected > 0
              ? 'filled'
              : 'outlined'
          }
        />

        <BulkAction
          selectedOrders={selectedOrders}
          bulkEndpoint={`${API_ENDPOINT}/api/org/orders/bulk-update/`}
          refetchOrders={resetAll}
          extractAsPdf={{
            fileName: `${merchant.name}.${new Date().toLocaleString()}.pdf`,
            tableTitle: 'Merchant Orders',
            fields_config: defaultMerchantReportFieldsConfig,
            subtitle: `Merchant: ${merchant.name} / ${merchant.phone_number}`,
            date: new Date().toLocaleString(),
            table_footer_rows: defaultMerchantFooter,
          }}
        />
      </div>
      <Switch
        checked={incompleteOrdersOnly}
        onChange={(e) => setIncompleteOrdersOnly(!incompleteOrdersOnly)}
      />{' '}
      Incomplete orders only
      <br />
      <Switch
        checked={includeFrozen}
        onChange={(e) => setIncludeFrozen(!includeFrozen)}
      />{' '}
        Include Frozen Orders
      <br />
      <ResponsiveOrdersListing
        ordersList={ordersList}
        selectedOrders={selectedOrders}
        updateSelectedOrders={updateSelectedOrders}
        isLoading={isLoading}
        sorting={sorting}
        setSorting={setSorting}
        applyQuickFilter={(quickFilter: IQuickFilter | null) => ApplyQuickFilter(quickFilter, filters, setFilters)}
        sourceView="merchant-orders"
      />
      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={ordersCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
      {showingPickupTask && (
        <PickupTaskDialog
          onClose={() => setShowingPickupTask(null)}
          taskID={showingPickupTask}
        />
      )}
      {showingDeliveryBatch && (
        <DeliveryBatchDialog
          onClose={() => setShowingDeliveryBatch(null)}
          taskID={showingDeliveryBatch}
        />
      )}
    </Box>
  );
}

export default MerchantOrders;
